import { useState, useEffect } from 'react';
import { Socket } from 'socket.io-client';
import toast from 'react-hot-toast';

interface LotteryPanelProps {
  sessionId: string;
  socket: Socket | null;
  isConnected: boolean;
}

interface Participant {
  audienceId: string;
  nickname: string;
  wantsToParticipate: boolean;
}

interface LotteryResults {
  lotteryId: string;
  participants: Participant[];
  nonParticipants: Participant[];
  totalYes: number;
  totalNo: number;
  winnerId: string | null;
  winnerNickname: string | null;
  winners: { id: string; nickname: string }[];
  isActive: boolean;
}

type LotteryState = 'idle' | 'voting' | 'results';

const DEFAULT_TEXTS = {
  votingQuestion: 'Vuoi poter essere coinvolto?',
  buttonYes: 'Si si SIII!',
  buttonNo: 'NO, lasciatemi in pace!',
  winnerTitle: 'SEI STATO SCELTO!',
  winnerSubtitle: 'Preparati ad essere coinvolto!',
};

export function LotteryPanel({ sessionId, socket, isConnected }: LotteryPanelProps) {
  const [duration, setDuration] = useState(12);
  const [winnersCount, setWinnersCount] = useState(1);
  const [textsOpen, setTextsOpen] = useState(false);
  const [texts, setTexts] = useState({ ...DEFAULT_TEXTS });
  const [lotteryState, setLotteryState] = useState<LotteryState>('idle');
  const [activeLotteryId, setActiveLotteryId] = useState<string | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [nonParticipants, setNonParticipants] = useState<Participant[]>([]);
  const [, setWinnerId] = useState<string | null>(null);
  const [, setWinnerNickname] = useState<string | null>(null);
  const [winners, setWinners] = useState<{ id: string; nickname: string }[]>([]);

  // Ascolta eventi lottery
  useEffect(() => {
    if (!socket) return;

    const handleTick = (data: { lotteryId: string; remainingSeconds: number }) => {
      if (data.lotteryId === activeLotteryId) {
        setRemainingSeconds(data.remainingSeconds);
      }
    };

    const handleVoteSubmitted = (data: {
      lotteryId: string;
      participants: Participant[];
      nonParticipants: Participant[];
      totalYes: number;
      totalNo: number;
    }) => {
      if (data.lotteryId === activeLotteryId) {
        setParticipants(data.participants);
        setNonParticipants(data.nonParticipants);
      }
    };

    const handleClosed = (data: LotteryResults) => {
      if (data.lotteryId === activeLotteryId) {
        setLotteryState('results');
        setRemainingSeconds(null);
        setParticipants(data.participants);
        setNonParticipants(data.nonParticipants);
        setWinnerId(data.winnerId);
        setWinnerNickname(data.winnerNickname);
        setWinners(data.winners || []);
      }
    };

    const handleReset = () => {
      resetLocalState();
    };

    socket.on('lottery:tick', handleTick);
    socket.on('lottery:vote-submitted', handleVoteSubmitted);
    socket.on('lottery:closed', handleClosed);
    socket.on('lottery:reset', handleReset);

    return () => {
      socket.off('lottery:tick', handleTick);
      socket.off('lottery:vote-submitted', handleVoteSubmitted);
      socket.off('lottery:closed', handleClosed);
      socket.off('lottery:reset', handleReset);
    };
  }, [socket, activeLotteryId]);

  const resetLocalState = () => {
    setLotteryState('idle');
    setActiveLotteryId(null);
    setRemainingSeconds(null);
    setParticipants([]);
    setNonParticipants([]);
    setWinnerId(null);
    setWinnerNickname(null);
    setWinners([]);
  };

  const handleStartLottery = async () => {
    if (!isConnected) {
      toast.error('Socket non connesso');
      return;
    }

    try {
      const response = await fetch('/api/lottery/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          durationSeconds: duration,
          winnersCount,
          texts,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Errore avvio lotteria');
      }

      const { lottery } = await response.json();
      setActiveLotteryId(lottery.id);
      setLotteryState('voting');
      setRemainingSeconds(duration);
      setParticipants([]);
      setNonParticipants([]);

      toast.success('Lotteria avviata!');
    } catch (error: any) {
      console.error('[LotteryPanel] Error starting lottery:', error);
      toast.error(error.message || 'Errore avvio lotteria');
    }
  };

  const handleReset = async () => {
    if (activeLotteryId) {
      try {
        await fetch(`/api/lottery/${activeLotteryId}/reset`, {
          method: 'POST',
        });
      } catch (error) {
        console.error('[LotteryPanel] Error resetting lottery:', error);
      }
    }

    resetLocalState();

    // Torna a standby sul display
    if (socket && isConnected) {
      socket.emit('admin:set-projection', {
        sessionId,
        mode: { type: 'standby' },
      });
    }

    toast.success('Lotteria resettata');
  };

  const handleForceClose = async () => {
    if (!activeLotteryId) return;

    try {
      const response = await fetch(`/api/lottery/${activeLotteryId}/close`, {
        method: 'POST',
      });

      if (response.ok) {
        const data = await response.json();
        setLotteryState('results');
        setRemainingSeconds(null);
        setWinnerId(data.winnerId);
        setWinnerNickname(data.winnerNickname);
        setWinners(data.winners || []);
        toast.success('Lotteria chiusa');
      }
    } catch (error) {
      console.error('[LotteryPanel] Error closing lottery:', error);
      toast.error('Errore chiusura lotteria');
    }
  };

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
      <h3 className="text-xl font-bold text-white mb-4">Lotteria</h3>

      {lotteryState === 'idle' && (
        <>
          {/* Durata e numero vincitori */}
          <div className="mb-6 flex gap-6">
            <div>
              <label className="text-gray-400 text-sm font-semibold block mb-2">
                Durata voto (secondi)
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={duration}
                onChange={(e) => setDuration(e.target.value as any)}
                onBlur={(e) => { const n = parseInt(e.target.value); setDuration(isNaN(n) ? 12 : Math.max(3, Math.min(30, n))); }}
                className="w-24 bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <span className="text-gray-500 text-sm ml-2">secondi</span>
            </div>
            <div>
              <label className="text-gray-400 text-sm font-semibold block mb-2">
                N. vincitori
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={winnersCount}
                onChange={(e) => setWinnersCount(e.target.value as any)}
                onBlur={(e) => { const n = parseInt(e.target.value); setWinnersCount(isNaN(n) ? 1 : Math.max(1, Math.min(20, n))); }}
                className="w-24 bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          {/* Testi personalizzabili (collapsible) */}
          <div className="mb-6 border border-gray-600 rounded-lg overflow-hidden">
            <button
              onClick={() => setTextsOpen(!textsOpen)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-700/50 hover:bg-gray-700 transition-colors text-left"
            >
              <span className="text-gray-300 text-sm font-semibold">Testi personalizzabili</span>
              <span className={`text-gray-400 transition-transform ${textsOpen ? 'rotate-180' : ''}`}>▼</span>
            </button>
            {textsOpen && (
              <div className="p-4 space-y-3 bg-gray-700/30">
                <div>
                  <label className="text-gray-400 text-xs block mb-1">Domanda (audience)</label>
                  <input
                    type="text"
                    value={texts.votingQuestion}
                    onChange={(e) => setTexts(t => ({ ...t, votingQuestion: e.target.value }))}
                    className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-gray-400 text-xs block mb-1">Bottone Sì</label>
                    <input
                      type="text"
                      value={texts.buttonYes}
                      onChange={(e) => setTexts(t => ({ ...t, buttonYes: e.target.value }))}
                      className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs block mb-1">Bottone No</label>
                    <input
                      type="text"
                      value={texts.buttonNo}
                      onChange={(e) => setTexts(t => ({ ...t, buttonNo: e.target.value }))}
                      className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-xs block mb-1">Titolo vincitore (audience)</label>
                  <input
                    type="text"
                    value={texts.winnerTitle}
                    onChange={(e) => setTexts(t => ({ ...t, winnerTitle: e.target.value }))}
                    className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs block mb-1">Sottotitolo vincitore (audience)</label>
                  <input
                    type="text"
                    value={texts.winnerSubtitle}
                    onChange={(e) => setTexts(t => ({ ...t, winnerSubtitle: e.target.value }))}
                    className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Pulsanti */}
          <div className="flex gap-3">
            <button
              onClick={handleStartLottery}
              disabled={!isConnected}
              className="flex-1 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-3 px-4 rounded-lg font-bold transition-all"
            >
              🎲 Avvia Lotteria
            </button>
          </div>
        </>
      )}

      {lotteryState === 'voting' && (
        <div className="py-4">
          {/* Timer */}
          <div className="text-center mb-6">
            <div className="text-6xl mb-2 font-bold text-yellow-400">
              {remainingSeconds !== null ? remainingSeconds : '...'}
            </div>
            <p className="text-xl text-white font-bold">Votazione in corso</p>
          </div>

          {/* Due colonne: Sì e No */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Colonna Sì */}
            <div className="bg-green-900/30 border border-green-600 rounded-lg p-4">
              <h4 className="text-green-400 font-bold mb-3 flex items-center gap-2">
                <span className="text-xl">✓</span>
                Sì ({participants.length})
              </h4>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {participants.length === 0 ? (
                  <p className="text-gray-500 text-sm italic">Nessuno ancora</p>
                ) : (
                  participants.map((p) => (
                    <div
                      key={p.audienceId}
                      className="text-white text-sm bg-green-800/30 px-2 py-1 rounded"
                    >
                      {p.nickname}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Colonna No */}
            <div className="bg-red-900/30 border border-red-600 rounded-lg p-4">
              <h4 className="text-red-400 font-bold mb-3 flex items-center gap-2">
                <span className="text-xl">✗</span>
                No ({nonParticipants.length})
              </h4>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {nonParticipants.length === 0 ? (
                  <p className="text-gray-500 text-sm italic">Nessuno ancora</p>
                ) : (
                  nonParticipants.map((p) => (
                    <div
                      key={p.audienceId}
                      className="text-white text-sm bg-red-800/30 px-2 py-1 rounded"
                    >
                      {p.nickname}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Pulsante chiudi */}
          <div className="flex gap-3">
            <button
              onClick={handleForceClose}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-6 rounded-lg font-semibold transition-all"
            >
              Chiudi ora ed estrai
            </button>
          </div>
        </div>
      )}

      {lotteryState === 'results' && (
        <div className="text-center py-4">
          {winners.length > 0 ? (
            <>
              <div className="text-6xl mb-4">🎉</div>
              <p className="text-2xl text-white font-bold mb-2">
                {winners.length === 1 ? 'Vincitore:' : 'Vincitori:'}
              </p>
              {winners.map((w) => (
                <p key={w.id} className="text-3xl text-yellow-400 font-bold mb-2">{w.nickname}</p>
              ))}
              <div className="mb-6" />
            </>
          ) : (
            <>
              <div className="text-6xl mb-4">😢</div>
              <p className="text-2xl text-white font-bold mb-2">Nessun partecipante</p>
              <p className="text-gray-400 mb-6">Nessuno ha votato "Sì"</p>
            </>
          )}

          {/* Riepilogo voti */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-green-900/30 border border-green-600 rounded-lg p-3">
              <span className="text-green-400 font-bold text-xl">{participants.length}</span>
              <span className="text-gray-400 text-sm ml-2">Sì</span>
            </div>
            <div className="bg-red-900/30 border border-red-600 rounded-lg p-3">
              <span className="text-red-400 font-bold text-xl">{nonParticipants.length}</span>
              <span className="text-gray-400 text-sm ml-2">No</span>
            </div>
          </div>

          <button
            onClick={handleReset}
            className="bg-purple-600 hover:bg-purple-700 text-white py-3 px-8 rounded-lg font-bold transition-all"
          >
            Reset
          </button>
        </div>
      )}

      {!isConnected && lotteryState === 'idle' && (
        <div className="mt-4 p-3 bg-yellow-900/30 border border-yellow-600 rounded-lg">
          <p className="text-yellow-300 text-sm">
            Socket non connesso. La lotteria non può essere avviata.
          </p>
        </div>
      )}
    </div>
  );
}
